import PackageInfo from '../../../../package.json';

import {
  AppLogger,
  DatadogClient,
  defaultRUMConfig,
} from '@workivate/tho-web-shared';
import type {
  RumInitConfiguration,
  LogsInitConfiguration,
} from '@workivate/tho-web-shared';
import type { RumEvent } from '@datadog/browser-rum';

const commonDatadogConfig = {
  service: 'wa-hermes',
  env: window.WAM.ENV.env === 'live' ? 'prod' : window.WAM.ENV.env, // Use 'prod' instead of 'live' for DataDog env tag to make it consistent with other environments,
  version: `${PackageInfo.version}_${window.WAM.ENV.env}`, // This version must match with the version that is referenced in web_cicd.yml
  clientToken: window.WAM.ENV.ddClientToken ?? '',
};

const dataDogRUMConfig: RumInitConfiguration = {
  ...commonDatadogConfig,
  applicationId: window.WAM.ENV.ddAppId ?? '',

  allowedTracingUrls: [
    {
      propagatorTypes: ['datadog'],
      match: /https?:\/\/.*\.lifeworks\.com\/tenancy-lookup-api\/tenant$/,
    },
    {
      propagatorTypes: ['datadog'],
      match: /https?:\/\/.*\.lifeworks\.com\/wellbeing-graphql-api\/graphql$/,
    },
    {
      propagatorTypes: ['datadog'],
      match: /https:\/\/global\.enterprise(?:\.[^.]+)?\.lifeworks\.com/,
    },
  ],
  defaultPrivacyLevel: 'mask',
  sessionReplaySampleRate: 20,
  sessionSampleRate: 100,

  trackLongTasks: true,
  trackUserInteractions: true,

  excludedActivityUrls: [url => url.includes('google')],

  beforeSend: (event: RumEvent, context) => {
    // First, run the default beforeSend logic from tho-web-shared
    // This handles filtering resources, removing sensitive data, and excluding certain errors
    const defaultBeforeSendResult = defaultRUMConfig.beforeSend?.(
      event,
      context,
    );

    // If the default beforeSend returns false, don't send the event
    if (defaultBeforeSendResult === false) {
      return false;
    }

    // Additional filtering: exclude Microsoft Omnichannel chat widget interactions
    if (event.type === 'action') {
      const actionSelector = event['_dd']?.action?.target?.selector;

      if (typeof actionSelector === 'string') {
        // Check for chat widget container class or webchat-specific elements
        if (
          actionSelector.includes('ccs-chat-widget') ||
          actionSelector.includes('webchat__') ||
          actionSelector.includes('microsoft-omnichannel')
        ) {
          return false; // Prevent logging this action
        }
      }
    }

    return true;
  },
};

const dataDogLogsConfig: LogsInitConfiguration = {
  ...commonDatadogConfig,
};

export const initialiseDataDog = () => {
  DatadogClient.initDataDog(dataDogLogsConfig, dataDogRUMConfig);

  AppLogger.configure({
    isProduction: IS_PRODUCTION,
    datadogLogger: (message, context) => {
      const ctx = (context ?? {}) as { level?: string; error?: string };

      if (ctx.error) {
        DatadogClient.addError(new Error(ctx.error), {
          logMessage: message,
          ...(ctx.level && { level: ctx.level }),
        });
      }

      DatadogClient.logMessage(message, context);
    },
  });
};
